import { useEffect, useState } from 'react'

// Site-wide viewer identity, resolved the one canonical way so writers (owner stamping)
// and readers (the nav ownership gate) never disagree: slow pages expose the signed-in
// user's id on window.App.config.userId, fast pages on window.pageConfig.currentUserId
// (which lands after /load-services.js). Normalized to a string because the two sources
// and the localStorage JSON round-trip can drift between number and string, and a bare
// === would then miss a match for the very same user.
export function resolveViewerId() {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = window.App?.config?.userId ?? window.pageConfig?.currentUserId
  return raw == null ? null : String(raw)
}

// Reactive viewer id for render paths. Seeds from whatever is already resolved, then
// updates once the client config lands. /load-services.js fires both window-config-loaded
// and full-page-config-loaded, so listen to both (some pages dispatch only one). Never
// downgrades a resolved id back to null, so a late/duplicate event cannot blank it out.
export default function useViewerId() {
  const [viewerId, setViewerId] = useState(resolveViewerId)

  useEffect(() => {
    function sync() {
      const next = resolveViewerId()
      if (next != null) {
        setViewerId(next)
      }
    }

    // Catch a value that landed between the initial render and this effect attaching.
    sync()
    window.addEventListener('window-config-loaded', sync)
    window.addEventListener('full-page-config-loaded', sync)

    return () => {
      window.removeEventListener('window-config-loaded', sync)
      window.removeEventListener('full-page-config-loaded', sync)
    }
  }, [])

  return viewerId
}
